import api from './api';
import { taxService } from '../utils/taxCalculator';

/**
 * Payment Service for GETTRX One Payment Integration
 * 
 * This service handles all payment operations using GETTRX One platform.
 * Cards are tokenized on the frontend using GETTRX WebElement SDK,
 * and payment tokens (pt_xxx) are sent to the backend for processing.
 * 
 * IMPORTANT: This service NO LONGER accepts raw card data.
 * Use gettrxService to tokenize cards before calling payment methods.
 */
export const paymentService = {
    // ============================================================================
    // GETTRX PAYMENT PROCESSING
    // ============================================================================

    /**
     * Process payment using GETTRX payment token
     * @param {Object} paymentData - Payment data with token
     * @param {string} paymentData.paymentToken - GETTRX payment token (pt_xxx)
     * @param {Object} userInfo - Current user info
     */
    async processCreditCardPayment(paymentData, userInfo = null) {
        try {
            // If this is a message purchase, use the specific message endpoint
            if (paymentData.messageId) {
                return await this.createMessagePayment(paymentData.messageId, paymentData, userInfo);
            }
            
            // If this is a post purchase, use the specific post endpoint
            if (paymentData.postId) {
                return await this.createPostPayment(paymentData.postId, paymentData, userInfo);
            }
            
            // General credit card payment endpoint (using token)
            const response = await api.post('payments/credit-card', {
                // Payment details
                amount: paymentData.amount,
                description: paymentData.description,
                
                // GETTRX payment token (replaces raw card data)
                paymentToken: paymentData.paymentToken,
                
                // Billing address (still needed for AVS verification)
                billingName: paymentData.billingName || paymentData.cardholderName,
                billingAddress: paymentData.billingAddress || paymentData.billingStreet,
                billingCity: paymentData.billingCity,
                billingState: paymentData.billingState,
                billingZip: paymentData.billingZip,
                billingCountry: paymentData.billingCountry || 'US',
                
                // Context information
                messageId: paymentData.messageId,
                postId: paymentData.postId,
                creatorId: paymentData.creatorId,
                customerReference: paymentData.customerReference
            });
            return response.data;
        } catch (error) {
            console.error('Error processing credit card payment:', error);
            throw this._handlePaymentError(error);
        }
    },

    // Subscription Payments - Updated for GETTRX
    async processSubscriptionPayment(subscriptionData) {
        try {
            const response = await api.post('payments/subscription', {
                // Subscription details
                amount: subscriptionData.amount,
                subscriptionPlanId: subscriptionData.subscriptionPlanId,
                creatorId: subscriptionData.creatorId,
                
                // GETTRX payment token (replaces raw card data)
                paymentToken: subscriptionData.paymentToken,
                
                // Billing address
                billingName: subscriptionData.billingName || subscriptionData.cardholderName,
                billingAddress: subscriptionData.billingAddress || subscriptionData.billingStreet,
                billingCity: subscriptionData.billingCity,
                billingState: subscriptionData.billingState,
                billingZip: subscriptionData.billingZip,
                billingCountry: subscriptionData.billingCountry || 'US',
                
                // Subscription settings
                recurringFrequency: subscriptionData.recurringFrequency || 'monthly',
                maxRebills: subscriptionData.maxRebills || -1, // -1 for unlimited
                autoRebill: subscriptionData.autoRebill !== false // default true
            });
            return response.data;
        } catch (error) {
            console.error('Error processing subscription payment:', error);
            throw this._handlePaymentError(error);
        }
    },

    /**
     * Subscribe with stored payment method
     * Uses the new backend endpoint that processes payment and creates subscription atomically
     * 
     * @param {string} creatorId - The creator's user ID
     * @param {string} planId - The subscription plan ID
     * @param {string} storedPaymentMethodId - The stored payment method to charge
     * @param {Object} taxCalculation - Tax calculation details (baseAmount, taxAmount, totalAmount, taxRate)
     * @param {string} billingPeriod - The billing period (MONTH, THREE_MONTHS, SIX_MONTHS, YEAR)
     * @returns {Promise<Object>} Subscription details
     */
    async subscribeWithStoredPaymentMethod(creatorId, planId, storedPaymentMethodId, taxCalculation = null, billingPeriod = 'MONTH') {
        try {
            const requestBody = {
                storedPaymentMethodId,
                billingPeriod: billingPeriod || 'MONTH'
            };
            
            // Include tax calculation if provided
            if (taxCalculation) {
                requestBody.baseAmount = taxCalculation.baseAmount;
                requestBody.taxAmount = taxCalculation.taxAmount;
                requestBody.totalAmount = taxCalculation.totalAmount;
                requestBody.taxRate = taxCalculation.taxRate;
            }
            
            const response = await api.post(`/users/${creatorId}/subscribe/${planId}/pay`, requestBody);
            return {
                successful: true,
                subscription: response.data,
                responseText: 'Subscription created successfully',
                responseCode: 'SUCCESS'
            };
        } catch (error) {
            console.error('Error subscribing with stored payment method:', error);
            throw this._handlePaymentError(error);
        }
    },

    // Message Payments - Updated for GETTRX
    async createMessagePayment(messageId, paymentData, userInfo = null) {
        try {
            // Use provided userInfo (from AuthContext) or throw error if not provided
            if (!userInfo) {
                throw new Error('User authentication required for payment processing');
            }
            
            const currentUserInfo = userInfo;
            
            // Prepare request matching MessagePurchaseRequestDTO structure
            const requestData = {
                // Required IDs
                messageId: messageId,
                userId: currentUserInfo.id || currentUserInfo.userId,
                
                // Tax and amount information (always required)
                taxAmount: paymentData.taxCalculation?.taxAmount || 0,
                totalAmount: paymentData.taxCalculation?.totalAmount || paymentData.amount,
                
                // Optional fields
                promoCode: paymentData.promoCode,
                purchaseDescription: paymentData.description || 'Message purchase',
                
                // Save payment method flag
                savePaymentMethod: paymentData.savePaymentMethod || false
            };

            // Handle different payment methods
            if (paymentData.storedMethodId) {
                // Using stored payment method (backend will look up GETTRX IDs)
                requestData.storedPaymentMethodId = paymentData.storedMethodId;
                
                // Security fields
                requestData.userAgent = navigator.userAgent;
                requestData.customerEmail = currentUserInfo.email;
                if (paymentData.customerPhone) requestData.customerPhone = paymentData.customerPhone;
                if (paymentData.deviceFingerprint) requestData.deviceFingerprint = paymentData.deviceFingerprint;
                
            } else if (paymentData.paymentToken) {
                // Using GETTRX payment token (pt_xxx) - NEW PREFERRED METHOD
                requestData.paymentToken = paymentData.paymentToken;
                
                // Card details from tokenization (for saving payment method)
                if (paymentData.cardLast4) requestData.cardLast4 = paymentData.cardLast4;
                if (paymentData.cardBrand) requestData.cardBrand = paymentData.cardBrand;
                if (paymentData.cardExpMonth) requestData.cardExpMonth = paymentData.cardExpMonth;
                if (paymentData.cardExpYear) requestData.cardExpYear = paymentData.cardExpYear;
                
                // Billing information (for AVS verification)
                requestData.billingName = paymentData.billingName || paymentData.cardholderName;
                requestData.billingStreet = paymentData.billingStreet || paymentData.billingAddress;
                requestData.billingCity = paymentData.billingCity;
                requestData.billingState = paymentData.billingState;
                requestData.billingZip = paymentData.billingZip;
                requestData.billingCountry = paymentData.billingCountry || 'US';
                
                // Security fields
                requestData.customerEmail = currentUserInfo.email;
                requestData.userAgent = navigator.userAgent;
                if (paymentData.customerPhone) requestData.customerPhone = paymentData.customerPhone;
                if (paymentData.deviceFingerprint) requestData.deviceFingerprint = paymentData.deviceFingerprint;
                
            } else if (paymentData.cardNumber) {
                // DEPRECATED: Raw card data - Log warning and still process for backward compatibility
                console.warn('DEPRECATED: Raw card data sent to createMessagePayment. Use paymentToken instead.');
                
                requestData.cardNumber = paymentData.cardNumber;
                requestData.expirationMonth = parseInt(paymentData.expirationMonth);
                requestData.expirationYear = this._normalizeExpirationYear(paymentData.expirationYear);
                requestData.cvv = paymentData.cvv;
                
                requestData.billingName = paymentData.cardholderName || paymentData.billingName;
                requestData.billingStreet = paymentData.billingAddress || paymentData.billingStreet;
                requestData.billingCity = paymentData.billingCity;
                requestData.billingState = paymentData.billingState;
                requestData.billingZip = paymentData.billingZip;
                requestData.billingCountry = paymentData.billingCountry || 'US';
                
                requestData.customerEmail = currentUserInfo.email;
                requestData.userAgent = navigator.userAgent;
            } else {
                throw new Error('No valid payment method provided. Use paymentToken from GETTRX SDK.');
            }
            
            // Use the correct backend endpoint
            const response = await api.post(`messages/${messageId}/purchase`, requestData);
            return response.data;
        } catch (error) {
            console.error('Error creating message payment:', error);
            throw this._handlePaymentError(error);
        }
    },

    // Post Payments - Updated for GETTRX
    async createPostPayment(postId, paymentData, userInfo = null) {
        try {
            // Use provided userInfo (from AuthContext) or throw error if not provided
            if (!userInfo) {
                throw new Error('User authentication required for payment processing');
            }
            
            const currentUserInfo = userInfo;
            
            // Prepare request matching PostPurchaseRequestDTO structure
            const requestData = {
                // Required IDs
                postId: postId,
                userId: currentUserInfo.id || currentUserInfo.userId,
                
                // Required payment amount
                postPrice: paymentData.amount,
                
                // Tax and amount information (always required)
                taxAmount: paymentData.taxCalculation?.taxAmount || 0,
                totalAmount: paymentData.taxCalculation?.totalAmount || paymentData.amount,
                
                // Optional fields
                promoCode: paymentData.promoCode,
                purchaseDescription: paymentData.description || 'Post purchase',
                
                // Save payment method flag
                savePaymentMethod: paymentData.savePaymentMethod || false
            };

            // Handle different payment methods
            if (paymentData.storedMethodId) {
                // Using stored payment method (backend will look up GETTRX IDs)
                requestData.storedPaymentMethodId = paymentData.storedMethodId;
                
                requestData.email = currentUserInfo.email;
                if (paymentData.customerPhone) requestData.phone = paymentData.customerPhone;
                
            } else if (paymentData.paymentToken) {
                // Using GETTRX payment token (pt_xxx) - NEW PREFERRED METHOD
                requestData.paymentToken = paymentData.paymentToken;
                
                // Card details from tokenization (for saving payment method)
                if (paymentData.cardLast4) requestData.cardLast4 = paymentData.cardLast4;
                if (paymentData.cardBrand) requestData.cardBrand = paymentData.cardBrand;
                if (paymentData.cardExpMonth) requestData.cardExpMonth = paymentData.cardExpMonth;
                if (paymentData.cardExpYear) requestData.cardExpYear = paymentData.cardExpYear;
                
                // Billing information
                requestData.cardholderName = paymentData.billingName || paymentData.cardholderName;
                requestData.addressLine1 = paymentData.billingStreet || paymentData.billingAddress;
                requestData.city = paymentData.billingCity;
                requestData.state = paymentData.billingState;
                requestData.zipCode = paymentData.billingZip;
                requestData.countryCode = paymentData.billingCountry || 'US';
                
                requestData.email = currentUserInfo.email;
                if (paymentData.customerPhone) requestData.phone = paymentData.customerPhone;
                
            } else if (paymentData.cardNumber) {
                // DEPRECATED: Raw card data
                console.warn('DEPRECATED: Raw card data sent to createPostPayment. Use paymentToken instead.');
                
                requestData.cardNumber = paymentData.cardNumber;
                requestData.expirationMonth = parseInt(paymentData.expirationMonth);
                requestData.expirationYear = this._normalizeExpirationYear(paymentData.expirationYear);
                requestData.cvv = paymentData.cvv;
                
                requestData.cardholderName = paymentData.cardholderName || paymentData.billingName;
                requestData.addressLine1 = paymentData.billingAddress || paymentData.billingStreet;
                requestData.city = paymentData.billingCity;
                requestData.state = paymentData.billingState;
                requestData.zipCode = paymentData.billingZip;
                requestData.countryCode = paymentData.billingCountry || 'US';
                
                requestData.email = currentUserInfo.email;
            } else {
                throw new Error('No valid payment method provided. Use paymentToken from GETTRX SDK.');
            }
            
            // Use the correct backend endpoint
            const response = await api.post(`posts/${postId}/purchase`, requestData);
            return response.data;
        } catch (error) {
            console.error('Error creating post payment:', error);
            throw this._handlePaymentError(error);
        }
    },

    // ============================================================================
    // TIP PAYMENT METHODS - UPDATED FOR GETTRX
    // ============================================================================
    
    /**
     * Create tip for a specific post (primarily for TARGET goals)
     * @param {string} postId - Post ID to tip for
     * @param {Object} tipData - Tip payment data
     * @param {number} tipData.amount - Tip amount
     * @param {string} tipData.message - Optional tip message
     * @param {string} tipData.tipperId - User making the tip
     * @param {string} tipData.creatorId - Creator receiving the tip
     * @param {string} tipData.paymentToken - GETTRX payment token (pt_xxx)
     * @returns {Promise} Tip response
     */
    async tipPost(postId, tipData) {
        try {
            const amount = parseFloat(tipData.amount);
            const taxAmount = 0.00; // No tax for tips currently
            const totalAmount = amount + taxAmount;

            const requestData = {
                // Post ID - required by PostTipRequestDTO
                postId: postId,
                
                // Tip details
                amount: amount,
                currency: 'USD',
                message: tipData.message || '',
                isPublic: tipData.isPublic !== undefined ? tipData.isPublic : true,
                
                // User IDs
                tipperId: tipData.tipperId,
                creatorId: tipData.creatorId,
                
                // Tax and total
                taxAmount: taxAmount,
                totalAmount: totalAmount,
                
                // TARGET goal tip differentiation
                isTargetGoalTip: tipData.isTargetGoalTip || false,
                
                // Customer information
                email: tipData.email,
                phone: tipData.phone && tipData.phone.trim() !== '' ? tipData.phone : null,
                
                // Security and tracking
                userAgent: navigator.userAgent,
                deviceFingerprint: tipData.deviceFingerprint
            };

            // Handle different payment methods
            if (tipData.storedPaymentMethodId) {
                // Using stored payment method (backend will look up GETTRX IDs)
                requestData.storedPaymentMethodId = tipData.storedPaymentMethodId;
            } else if (tipData.paymentToken) {
                // Using GETTRX payment token - NEW PREFERRED METHOD
                requestData.paymentToken = tipData.paymentToken;
                
                // Billing information
                requestData.cardholderName = tipData.cardholderName || tipData.billingName;
                requestData.addressLine1 = tipData.billingAddress || tipData.billingStreet;
                requestData.city = tipData.billingCity;
                requestData.state = tipData.billingState;
                requestData.zipCode = tipData.billingZip;
                requestData.countryCode = tipData.billingCountry || 'US';
            } else if (tipData.cardNumber) {
                // DEPRECATED: Raw card data
                console.warn('DEPRECATED: Raw card data sent to tipPost. Use paymentToken instead.');
                
                requestData.cardNumber = tipData.cardNumber;
                requestData.expirationMonth = parseInt(tipData.expirationMonth);
                requestData.expirationYear = parseInt(tipData.expirationYear);
                requestData.cvv = tipData.cvv;
                requestData.cardholderName = tipData.cardholderName;
                requestData.addressLine1 = tipData.billingAddress;
                requestData.city = tipData.billingCity;
                requestData.state = tipData.billingState;
                requestData.zipCode = tipData.billingZip;
                requestData.countryCode = tipData.billingCountry || 'US';
            } else {
                throw new Error('No valid payment method provided. Use paymentToken from GETTRX SDK.');
            }

            console.log('Sending tip request for post:', postId, requestData);
            const response = await api.post(`tips/post/${postId}`, requestData);
            
            console.log('Post tip successful:', response.data);
            return response.data;
            
        } catch (error) {
            console.error('Error creating post tip:', error);
            if (error.response?.data) {
                console.error('Backend error details:', error.response.data);
            }
            throw error;
        }
    },

    /**
     * Create tip for a specific message
     * @param {string} messageId - Message ID to tip for
     * @param {Object} tipData - Tip payment data (same structure as tipPost)
     * @returns {Promise} Tip response
     */
    async tipMessage(messageId, tipData) {
        try {
            const requestData = {
                // Tip details
                tipperId: tipData.tipperId,
                creatorId: tipData.creatorId,
                amount: parseFloat(tipData.amount),
                message: tipData.message || '',
                
                // Customer information
                email: tipData.email,
                phone: tipData.phone,
                
                // Security
                userAgent: navigator.userAgent,
                deviceFingerprint: tipData.deviceFingerprint
            };

            // Handle different payment methods
            if (tipData.storedPaymentMethodId) {
                // Using stored payment method (backend will look up GETTRX IDs)
                requestData.storedPaymentMethodId = tipData.storedPaymentMethodId;
            } else if (tipData.paymentToken) {
                requestData.paymentToken = tipData.paymentToken;
                requestData.cardholderName = tipData.cardholderName;
                requestData.addressLine1 = tipData.billingAddress;
                requestData.city = tipData.billingCity;
                requestData.state = tipData.billingState;
                requestData.zipCode = tipData.billingZip;
                requestData.countryCode = tipData.billingCountry || 'US';
            } else if (tipData.paymentDetails?.cardNumber) {
                // DEPRECATED: Raw card data from paymentDetails object
                console.warn('DEPRECATED: Raw card data sent to tipMessage. Use paymentToken instead.');
                
                requestData.cardNumber = tipData.paymentDetails.cardNumber;
                requestData.expirationMonth = parseInt(tipData.paymentDetails.expirationMonth);
                requestData.expirationYear = parseInt(tipData.paymentDetails.expirationYear);
                requestData.cvv = tipData.paymentDetails.cvv;
                requestData.cardholderName = tipData.paymentDetails.cardholderName;
                requestData.addressLine1 = tipData.paymentDetails.billingAddress;
                requestData.city = tipData.paymentDetails.billingCity;
                requestData.state = tipData.paymentDetails.billingState;
                requestData.zipCode = tipData.paymentDetails.billingZip;
                requestData.countryCode = tipData.paymentDetails.billingCountry || 'US';
            } else {
                throw new Error('No valid payment method provided. Use paymentToken from GETTRX SDK.');
            }

            const response = await api.post(`tips/message/${messageId}`, requestData);
            
            console.log('Message tip successful:', response.data);
            return response.data;
            
        } catch (error) {
            console.error('Error creating message tip:', error);
            throw error;
        }
    },

    /**
     * Create general tip for a creator (from their profile/menu)
     * @param {string} creatorId - Creator ID to tip
     * @param {Object} tipData - Tip payment data with paymentToken from GETTRX SDK or storedPaymentMethodId
     * @returns {Promise} Tip response
     */
    async tipCreatorGeneral(creatorId, tipData) {
        try {
            const amount = parseFloat(tipData.amount);
            const taxAmount = 0.00; // No tax for tips currently
            const totalAmount = amount + taxAmount;

            const requestData = {
                // Tip details
                tipperId: tipData.tipperId,
                creatorId: creatorId,
                amount: amount,
                currency: 'USD',
                message: tipData.message || '',
                isPublic: tipData.isPublic !== undefined ? tipData.isPublic : true,
                
                // Tax and total
                taxAmount: taxAmount,
                totalAmount: totalAmount,
                
                // Customer information
                email: tipData.email,
                phone: tipData.phone && tipData.phone.trim() !== '' ? tipData.phone : null,
                
                // Security and tracking
                userAgent: navigator.userAgent,
                deviceFingerprint: tipData.deviceFingerprint
            };

            // Handle different payment methods
            if (tipData.storedPaymentMethodId) {
                // Using stored payment method (backend will look up GETTRX IDs)
                requestData.storedPaymentMethodId = tipData.storedPaymentMethodId;
            } else if (tipData.paymentToken) {
                // GETTRX payment token (preferred for new cards)
                requestData.paymentToken = tipData.paymentToken;
                requestData.cardholderName = tipData.cardholderName;
                requestData.addressLine1 = tipData.billingAddress;
                requestData.city = tipData.billingCity;
                requestData.state = tipData.billingState;
                requestData.zipCode = tipData.billingZip;
                requestData.countryCode = tipData.billingCountry || 'US';
            } else if (tipData.paymentDetails?.cardNumber) {
                // DEPRECATED: Raw card data
                console.warn('DEPRECATED: Raw card data sent to tipCreatorGeneral. Use paymentToken instead.');
                
                requestData.cardNumber = tipData.paymentDetails.cardNumber;
                requestData.expirationMonth = parseInt(tipData.paymentDetails.expirationMonth);
                requestData.expirationYear = parseInt(tipData.paymentDetails.expirationYear);
                requestData.cvv = tipData.paymentDetails.cvv;
                requestData.cardholderName = tipData.paymentDetails.cardholderName;
                requestData.addressLine1 = tipData.paymentDetails.billingAddress;
                requestData.city = tipData.paymentDetails.billingCity;
                requestData.state = tipData.paymentDetails.billingState;
                requestData.zipCode = tipData.paymentDetails.billingZip;
                requestData.countryCode = tipData.paymentDetails.billingCountry || 'US';
            } else {
                throw new Error('No valid payment method provided. Use paymentToken from GETTRX SDK or storedPaymentMethodId.');
            }

            const response = await api.post(`tips/creator/${creatorId}`, requestData);
            
            console.log('General creator tip successful:', response.data);
            return response.data;
            
        } catch (error) {
            console.error('Error creating general tip:', error);
            throw error;
        }
    },

    /**
     * Create tip for a specific conversation
     * @param {string} conversationId - Conversation ID to tip for
     * @param {Object} tipData - Tip payment data with paymentToken from GETTRX SDK or storedPaymentMethodId
     * @returns {Promise} Tip response
     */
    async tipConversation(conversationId, tipData) {
        try {
            const amount = parseFloat(tipData.amount);
            const taxAmount = 0.00; // No tax for tips currently
            const totalAmount = amount + taxAmount;

            const requestData = {
                // Conversation ID (required for validation, even though it's in path)
                conversationId: conversationId,
                
                // Tip details
                tipperId: tipData.tipperId,
                creatorId: tipData.creatorId,
                amount: amount,
                currency: 'USD',
                message: tipData.message || '',
                isPublic: tipData.isPublic !== undefined ? tipData.isPublic : true,
                
                // Tax and total
                taxAmount: taxAmount,
                totalAmount: totalAmount,
                
                // Customer information
                email: tipData.email,
                phone: tipData.phone && tipData.phone.trim() !== '' ? tipData.phone : null,
                
                // Security and tracking
                userAgent: navigator.userAgent,
                deviceFingerprint: tipData.deviceFingerprint
            };

            // Handle different payment methods
            if (tipData.storedPaymentMethodId) {
                // Using stored payment method (backend will look up GETTRX IDs)
                requestData.storedPaymentMethodId = tipData.storedPaymentMethodId;
            } else if (tipData.paymentToken) {
                // GETTRX payment token (preferred for new cards)
                requestData.paymentToken = tipData.paymentToken;
                requestData.cardholderName = tipData.cardholderName;
                requestData.addressLine1 = tipData.billingAddress;
                requestData.city = tipData.billingCity;
                requestData.state = tipData.billingState;
                requestData.zipCode = tipData.billingZip;
                requestData.countryCode = tipData.billingCountry || 'US';
            } else if (tipData.cardNumber) {
                // DEPRECATED: Raw card data (direct mapping)
                console.warn('DEPRECATED: Raw card data sent to tipConversation. Use paymentToken instead.');
                
                requestData.cardNumber = tipData.cardNumber;
                requestData.expirationMonth = parseInt(tipData.expirationMonth);
                requestData.expirationYear = parseInt(tipData.expirationYear);
                requestData.cvv = tipData.cvv;
                requestData.cardholderName = tipData.cardholderName;
                requestData.addressLine1 = tipData.billingAddress;
                requestData.city = tipData.billingCity;
                requestData.state = tipData.billingState;
                requestData.zipCode = tipData.billingZip;
                requestData.countryCode = tipData.billingCountry || 'US';
            } else {
                throw new Error('No valid payment method provided. Use paymentToken from GETTRX SDK or storedPaymentMethodId.');
            }

            const response = await api.post(`tips/conversation/${conversationId}`, requestData);
            
            console.log('Conversation tip successful:', response.data);
            return response.data;
            
        } catch (error) {
            console.error('Error creating conversation tip:', error);
            throw error;
        }
    },

    // ============================================================================
    // LEGACY TIP METHODS (DEPRECATED - Use new tip methods above)
    // ============================================================================
    
    // OLD: Tip Payments - Updated for Orbital Pay
    async createTipPayment(postId, amount, paymentData) {
        console.warn('createTipPayment is deprecated. Use tipPost() instead.');
        try {
            // Convert old format to new format for backward compatibility
            const tipData = {
                tipperId: paymentData.userId,
                creatorId: paymentData.creatorId,
                amount: amount,
                message: paymentData.message || '',
                paymentDetails: paymentData
            };
            
            return await this.tipPost(postId, tipData);
        } catch (error) {
            console.error('Error creating tip payment (legacy):', error);
            throw error;
        }
    },

    // Subscription Creation - Updated for Orbital Pay
    async createSubscription(subscriptionPlanId, paymentData) {
        try {
            const enrichedPaymentData = {
                ...paymentData,
                subscriptionPlanId,
                customerReference: `SUB_${subscriptionPlanId}_${Date.now()}`
            };
            
            return await this.processSubscriptionPayment(enrichedPaymentData);
        } catch (error) {
            console.error('Error creating subscription:', error);
            throw error;
        }
    },

    // ============================================================================
    // PAYMENT STATUS & MANAGEMENT
    // ============================================================================

    // Payment Status Checking
    async checkPaymentStatus(paymentId) {
        try {
            const response = await api.get(`payments/${paymentId}/status`);
            return response.data;
        } catch (error) {
            console.error('Error checking payment status:', error);
            throw this._handlePaymentError(error);
        }
    },

    // Payment Status Polling (for real-time updates)
    async pollPaymentStatus(paymentId, maxAttempts = 10, intervalMs = 2000) {
        return new Promise((resolve, reject) => {
            let attempts = 0;
            const poll = async () => {
                try {
                    attempts++;
                    const status = await this.checkPaymentStatus(paymentId);
                    
                    // Payment completed successfully
                    if (status.status === 'completed' || status.status === 'approved') {
                        resolve(status);
                        return;
                    }
                    
                    // Payment failed
                    if (status.status === 'failed' || status.status === 'declined') {
                        reject(new Error(status.responseText || 'Payment failed'));
                        return;
                    }
                    
                    // Continue polling if still processing
                    if (attempts < maxAttempts && (status.status === 'processing' || status.status === 'pending')) {
                        setTimeout(poll, intervalMs);
                    } else {
                        reject(new Error('Payment polling timeout'));
                    }
                } catch (error) {
                    reject(error);
                }
            };
            
            poll();
        });
    },

    // Refund Processing
    async processRefund(paymentId, refundData) {
        try {
            const response = await api.post(`payments/${paymentId}/refund`, {
                refundAmount: refundData.amount,
                refundReason: refundData.reason,
                refundReference: `REF_${paymentId}_${Date.now()}`
            });
            return response.data;
        } catch (error) {
            console.error('Error processing refund:', error);
            throw this._handlePaymentError(error);
        }
    },

    // Subscription Management
    async cancelSubscription(subscriptionId) {
        try {
            const response = await api.delete(`subscriptions/${subscriptionId}`);
            return response.data;
        } catch (error) {
            console.error('Error cancelling subscription:', error);
            throw this._handlePaymentError(error);
        }
    },

    async updateSubscription(subscriptionId, updateData) {
        try {
            const response = await api.put(`subscriptions/${subscriptionId}`, updateData);
            return response.data;
        } catch (error) {
            console.error('Error updating subscription:', error);
            throw this._handlePaymentError(error);
        }
    },

    // ============================================================================
    // PAYMENT HISTORY & ANALYTICS
    // ============================================================================

    // Get Payment History
    async getPaymentHistory(filters = {}) {
        try {
            const params = new URLSearchParams();
            if (filters.page) params.append('page', filters.page);
            if (filters.size) params.append('size', filters.size);
            if (filters.status) params.append('status', filters.status);
            if (filters.startDate) params.append('startDate', filters.startDate);
            if (filters.endDate) params.append('endDate', filters.endDate);
            
            const response = await api.get(`payments?${params.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching payment history:', error);
            throw this._handlePaymentError(error);
        }
    },

    // Get Buyer Payments
    async getBuyerPayments(filters = {}) {
        try {
            const params = new URLSearchParams();
            if (filters.page) params.append('page', filters.page);
            if (filters.size) params.append('size', filters.size);
            
            const response = await api.get(`payments/buyer?${params.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching buyer payments:', error);
            throw this._handlePaymentError(error);
        }
    },

    // Get Seller Payments (Creator Earnings)
    async getSellerPayments(filters = {}) {
        try {
            const params = new URLSearchParams();
            if (filters.page) params.append('page', filters.page);
            if (filters.size) params.append('size', filters.size);
            
            const response = await api.get(`payments/seller?${params.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching seller payments:', error);
            throw this._handlePaymentError(error);
        }
    },

    // ============================================================================
    // CONVENIENCE METHODS (Updated for Orbital Pay)
    // ============================================================================

    // Purchase Post with Credit Card - Updated with Tax Calculation
    async purchasePost(postId, paymentData, userInfo = null) {
        try {
            // Return payment result - tax calculation is handled in backend
            const paymentResult = await this.createPostPayment(postId, paymentData, userInfo);
            
            return paymentResult;
        } catch (error) {
            console.error('Error purchasing post:', error);
            throw error;
        }
    },

    // Purchase Message with Credit Card - Updated with Tax Calculation
    async purchaseMessage(messageId, paymentData) {
        try {
            // Pre-calculate tax for display purposes (optional, since createMessagePayment also calculates)
            const userLocation = this._getUserLocation();
            const taxCalculation = await taxService.calculateTax(
                paymentData.amount, 
                userLocation,
                'MESSAGE_PURCHASE'
            );
            
            // Return both payment result and tax calculation for UI display
            const paymentResult = await this.createMessagePayment(messageId, paymentData);
            
            return {
                ...paymentResult,
                taxCalculation: taxCalculation
            };
        } catch (error) {
            console.error('Error purchasing message:', error);
            throw error;
        }
    },

    // OLD: Tip Creator with Credit Card (DEPRECATED)
    async tipCreator(postId, amount, paymentData) {
        console.warn('tipCreator is deprecated. Use tipPost() for post tips or tipCreatorGeneral() for general tips.');
        try {
            // For backward compatibility, assume this is a post tip
            const tipData = {
                tipperId: paymentData.userId,
                creatorId: paymentData.creatorId,
                amount: amount,
                message: paymentData.message || '',
                paymentDetails: paymentData
            };
            
            return await this.tipPost(postId, tipData);
        } catch (error) {
            console.error('Error tipping creator (legacy):', error);
            throw error;
        }
    },

    // Subscribe to Creator with Credit Card
    async subscribeToCreator(subscriptionPlanId, paymentData) {
        try {
            return await this.createSubscription(subscriptionPlanId, paymentData);
        } catch (error) {
            console.error('Error subscribing to creator:', error);
            throw error;
        }
    },

    // ============================================================================
    // UTILITY METHODS
    // ============================================================================

    // Validate Credit Card Data
    validateCreditCardData(paymentData) {
        const errors = {};

        // For stored payment methods, only CVV is required
        if (paymentData.storedMethodId) {
            // CVV is always required for security
            if (!paymentData.cvv) {
                errors.cvv = 'CVV is required';
            } else if (!/^\d{3,4}$/.test(paymentData.cvv)) {
                errors.cvv = 'Invalid CVV format';
            }

            // Amount is always required
            if (!paymentData.amount || paymentData.amount <= 0) {
                errors.amount = 'Valid amount is required';
            }
        } else {
            // For new cards, all fields are required
            if (!paymentData.cardNumber) {
                errors.cardNumber = 'Card number is required';
            } else if (!/^\d{13,19}$/.test(paymentData.cardNumber.replace(/\s/g, ''))) {
                errors.cardNumber = 'Invalid card number format';
            }

            if (!paymentData.expirationMonth) {
                errors.expirationMonth = 'Expiration month is required';
            } else if (!/^(0?[1-9]|1[0-2])$/.test(paymentData.expirationMonth)) {
                errors.expirationMonth = 'Invalid expiration month';
            }

            if (!paymentData.expirationYear) {
                errors.expirationYear = 'Expiration year is required';
            } else {
                const currentYear = new Date().getFullYear();
                // Normalize year to 4-digit format before validation
                const year = this._normalizeExpirationYear(paymentData.expirationYear);
                if (year < currentYear || year > currentYear + 20) {
                    errors.expirationYear = 'Invalid expiration year';
                }
            }

            if (!paymentData.cvv) {
                errors.cvv = 'CVV is required';
            } else if (!/^\d{3,4}$/.test(paymentData.cvv)) {
                errors.cvv = 'Invalid CVV format';
            }

            if (!paymentData.cardholderName) {
                errors.cardholderName = 'Cardholder name is required';
            }

            if (!paymentData.amount || paymentData.amount <= 0) {
                errors.amount = 'Valid amount is required';
            }
        }

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    },

    // Format Credit Card Number
    formatCardNumber(cardNumber) {
        const cleaned = cardNumber.replace(/\D/g, '');
        const formatted = cleaned.replace(/(\d{4})(?=\d)/g, '$1 ');
        return formatted;
    },

    // Get Card Type
    getCardType(cardNumber) {
        const cleaned = cardNumber.replace(/\D/g, '');
        
        if (/^4/.test(cleaned)) return 'visa';
        if (/^5[1-5]/.test(cleaned)) return 'mastercard';
        if (/^3[47]/.test(cleaned)) return 'amex';
        if (/^6/.test(cleaned)) return 'discover';
        
        return 'unknown';
    },

    // Get User Location for Tax Calculation
    _getUserLocation() {
        // Try to get location from localStorage (previously saved)
        const savedLocation = localStorage.getItem('userLocation');
        if (savedLocation) {
            try {
                return JSON.parse(savedLocation);
            } catch (e) {
                console.warn('Error parsing saved user location:', e);
            }
        }
        
        // Default to US if no location saved
        return {
            country: 'US',
            state: null,
            postalCode: null
        };
    },

    // Normalize expiration year to 4-digit format
    _normalizeExpirationYear(year) {
        if (!year) return null;
        
        const yearInt = parseInt(year);
        if (yearInt < 100) {
            // Convert 2-digit year to 4-digit (assuming 20xx)
            return yearInt + 2000;
        }
        return yearInt;
    },

    // Calculate Tax Preview (for UI display before purchase)
    async calculateTaxPreview(amount, userLocation = null, transactionType = 'MESSAGE_PURCHASE') {
        const location = userLocation || this._getUserLocation();
        return await taxService.calculateTax(amount, location, transactionType);
    },

    // Handle Payment Errors
    _handlePaymentError(error) {
        if (error.response) {
            const { status, data } = error.response;
            
            switch (status) {
                case 400:
                    return new Error(data.message || 'Invalid payment data');
                case 402:
                    return new Error(data.message || 'Payment failed - insufficient funds or declined');
                case 429:
                    return new Error('Too many payment attempts. Please try again later.');
                case 500:
                    return new Error('Payment service temporarily unavailable');
                default:
                    return new Error(data.message || 'Payment processing failed');
            }
        }
        
        return error;
    },

    // ============================================================================
    // TIP UTILITY METHODS
    // ============================================================================

    /**
     * Get tip history for a user
     * @param {string} userId - User ID to get tip history for
     * @param {Object} options - Filter options
     * @param {string} options.type - Filter: 'sent', 'received', or null for both
     * @param {number} options.page - Page number (0-based)
     * @param {number} options.size - Page size
     * @returns {Promise} Tip history with pagination
     */
    async getTipHistory(userId, options = {}) {
        try {
            const params = new URLSearchParams();
            if (options.type) params.append('type', options.type);
            if (options.page) params.append('page', options.page);
            if (options.size) params.append('size', options.size);
            
            const queryString = params.toString() ? `?${params.toString()}` : '';
            const response = await api.get(`tips/history/${userId}${queryString}`);
            
            return response.data;
        } catch (error) {
            console.error('Error fetching tip history:', error);
            throw error;
        }
    },

    /**
     * Get tip statistics for a creator
     * @param {string} creatorId - Creator ID to get statistics for
     * @returns {Promise} Tip statistics
     */
    async getTipStats(creatorId) {
        try {
            const response = await api.get(`tips/stats/${creatorId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching tip statistics:', error);
            throw error;
        }
    },

    /**
     * Validate tip amount
     * @param {number} amount - Amount to validate
     * @returns {Object} Validation result with isValid and error message
     */
    validateTipAmount(amount) {
        const numAmount = parseFloat(amount);
        
        if (isNaN(numAmount)) {
            return { isValid: false, error: 'Amount must be a valid number' };
        }
        
        if (numAmount <= 0) {
            return { isValid: false, error: 'Amount must be greater than $0' };
        }
        
        if (numAmount < 1.00) {
            return { isValid: false, error: 'Minimum tip amount is $1.00' };
        }
        
        if (numAmount > 10000) {
            return { isValid: false, error: 'Maximum tip amount is $10,000' };
        }
        
        // Check for reasonable decimal places (max 2)
        if (numAmount.toString().includes('.') && numAmount.toString().split('.')[1].length > 2) {
            return { isValid: false, error: 'Amount cannot have more than 2 decimal places' };
        }
        
        return { isValid: true };
    },

    /**
     * Format tip amount for display
     * @param {number} amount - Amount to format
     * @returns {string} Formatted amount string
     */
    formatTipAmount(amount) {
        const numAmount = parseFloat(amount);
        if (isNaN(numAmount)) return '$0.00';
        
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(numAmount);
    },

    /**
     * Prepare tip data for submission
     * @param {Object} tipFormData - Form data from UI
     * @param {string} currentUserId - Current user's ID
     * @returns {Object} Formatted tip data for API
     */
    prepareTipData(tipFormData, currentUserId) {
        return {
            tipperId: currentUserId,
            creatorId: tipFormData.creatorId,
            amount: parseFloat(tipFormData.amount),
            message: tipFormData.message?.trim() || '',
            paymentDetails: {
                cardNumber: tipFormData.cardNumber,
                expirationMonth: tipFormData.expirationMonth,
                expirationYear: tipFormData.expirationYear,
                cvv: tipFormData.cvv,
                cardholderName: tipFormData.cardholderName,
                billingAddress: tipFormData.billingAddress,
                billingCity: tipFormData.billingCity,
                billingState: tipFormData.billingState,
                billingZip: tipFormData.billingZip,
                billingCountry: tipFormData.billingCountry || 'US',
                email: tipFormData.email,
                phone: tipFormData.phone,
                deviceFingerprint: tipFormData.deviceFingerprint
            }
        };
    }
};